/**
 * Your numbers, and what this model thinks of them.
 *
 * The two halves are separated on purpose and the copy says which is which. The stats
 * are counts: they can be recomputed from the hand history and argued with on the
 * facts. The findings are opinions — that a number is outside the range winning
 * players usually sit in — and one of them rests on our own EV model rather than on a
 * solve, which is stated where it is shown rather than buried.
 */
import type { Stats, Derived, Finding } from "@engine/stats";

const fmt = (v: number | null, digits = 0, suffix = "") =>
  v === null || !Number.isFinite(v) ? "—" : `${v.toFixed(digits)}${suffix}`;

export function StatsPanel({ stats, derived, leaks }: {
  stats: Stats | null; derived: Derived | null; leaks: Finding[];
}) {
  if (!stats || !derived) return null;
  const thin = stats.hands < 30;

  return (
    <section className="panel">
      <h2>Your session</h2>
      <p className="muted small">
        {stats.hands} hand{stats.hands === 1 ? "" : "s"}
        {thin && " — too few for the numbers below to mean much yet"}
      </p>

      <dl className="statgrid">
        <Stat label="VPIP" hint="hands you put money in" value={fmt(derived.vpip, 0, "%")} />
        <Stat label="PFR" hint="hands you raised first in" value={fmt(derived.pfr, 0, "%")} />
        <Stat label="3-bet" hint="re-raises when facing a raise" value={fmt(derived.threeBet, 0, "%")} />
        <Stat label="WTSD" hint="showdowns per flop seen" value={fmt(derived.wtsd, 0, "%")} />
        <Stat label="W$SD" hint="showdowns won" value={fmt(derived.wsd, 0, "%")} />
        <Stat label="AF" hint="bets and raises per call, after the flop"
              value={derived.aggressionFactor === null ? "—"
                : derived.aggressionFactor === Infinity ? "∞" : derived.aggressionFactor.toFixed(1)} />
        <Stat label="C-bet" hint="flops you bet after raising" value={fmt(derived.cbet, 0, "%")} />
        <Stat label="bb/100" hint="big blinds won per hundred hands"
              value={fmt(derived.bb100, 1)} tone={(derived.bb100 ?? 0) >= 0 ? "up" : "down"} />
      </dl>

      <h3>What stands out</h3>
      {leaks.length === 0 ? (
        <p className="muted small">
          {thin
            ? "Play a few more hands and this will start pointing at things."
            : "Nothing outside the usual ranges. Keep going and it will find something."}
        </p>
      ) : (
        <ul className="leaks">
          {leaks.map((f) => (
            <li key={f.id} className={`leak leak-${f.severity}`}>
              <h4>{f.title}</h4>
              <p>{f.detail}</p>
              <p className="leak-advice">{f.advice}</p>
              <p className="muted small">
                From {f.sample} observation{f.sample === 1 ? "" : "s"}.
                {f.band[1] > 0 && ` Typical range ${f.band[0]}–${f.band[1]}.`}
              </p>
            </li>
          ))}
        </ul>
      )}
      <p className="muted small">
        These bands are where winning small-stakes players usually sit — our judgement,
        not a solve. The solved charts are in the trainer.
      </p>
    </section>
  );
}

function Stat({ label, hint, value, tone }: {
  label: string; hint: string; value: string; tone?: "up" | "down";
}) {
  return (
    <div className="stat">
      <dt>
        {label}
        <span className="stat-hint">{hint}</span>
      </dt>
      <dd className={`num${tone ? ` stat-${tone}` : ""}`}>{value}</dd>
    </div>
  );
}
